// Състоянието на редактора на правило: продуктите (картите на равенството),
// режимът на приоритета, коментарът и всичко изведено от тях — payload-ът за
// запис, sortIndex-ите за примера и пречките пред записа. Страницата само го
// сглобява с клавиатурата, пазача и мутациите.
//
// Всяка промяна вика `onTouch` (флагът за незапазени промени), зареждането —
// не. Payload-ът е СЪЩИЯТ като преди редизайна: { hint, members:
// [{ productId, coefficient, sortIndex }] }, без warehouseId.
import { useMemo, useRef, useState } from "react";
import type { InterchangeGroup, InterchangeGroupPayload, Product } from "../../../../api/inventory";
import {
  amountsFromCoefficients,
  coefficientsForSave,
  sortIndexesForSave,
  type PriorityMode,
} from "./interchangeRuleMath";
import type { InterchangeMemberDraft } from "./interchangeablesShared";

export const useInterchangeRuleDraft = (onTouch: () => void) => {
  const [members, setMembers] = useState<InterchangeMemberDraft[]>([]);
  const [hint, setHintState] = useState("");
  const [priorityMode, setPriorityModeState] = useState<PriorityMode>("oldest");
  // Редът на картите е пипан (преместване, нов продукт, смяна на режима) →
  // приоритетът се номерира наново; иначе заредените sortIndex-и остават.
  const [orderTouched, setOrderTouched] = useState(false);
  const nextKeyRef = useRef(1);

  const load = (group: InterchangeGroup) => {
    const amounts = amountsFromCoefficients(group.members.map((member) => member.coefficient));
    setHintState(group.hint ?? "");
    setPriorityModeState(
      group.members.some((member) => member.sortIndex != null) ? "order" : "oldest",
    );
    setOrderTouched(false);
    // BE ги връща вече подредени по приоритет (sortIndex, после id)
    setMembers(
      group.members.map((member, index) => ({
        key: nextKeyRef.current++,
        productId: member.productId,
        productName: member.productName,
        unit: member.unit,
        amount: amounts[index],
        savedCoefficient: member.coefficient,
        savedSortIndex: member.sortIndex,
      })),
    );
  };

  // Нов продукт влиза в края, равностоен на ПЪРВИЯ („1 кг A = 1 кг B") —
  // потребителят сменя само числото, ако не са 1:1.
  const addProducts = (products: Product[]) => {
    if (!products.length) return;
    onTouch();
    setOrderTouched(true);
    setMembers((current) => {
      const present = new Set(current.map((member) => member.productId));
      const firstAmount = current[0]?.amount;
      const defaultAmount = firstAmount != null && firstAmount > 0 ? firstAmount : 1;
      return [
        ...current,
        ...products
          .filter((product) => !present.has(product.id))
          .map((product) => ({
            key: nextKeyRef.current++,
            productId: product.id,
            productName: product.name,
            unit: product.unit ?? null,
            amount: defaultAmount,
            savedCoefficient: null,
            savedSortIndex: null,
          })),
      ];
    });
  };

  const removeMember = (key: number) => {
    onTouch();
    setMembers((current) => current.filter((member) => member.key !== key));
  };

  const setAmount = (key: number, amount: number | null) => {
    onTouch();
    setMembers((current) =>
      current.map((member) =>
        member.key === key ? { ...member, amount, savedCoefficient: null } : member,
      ),
    );
  };

  // Премества картата на позиция `toIndex` (стрелките, Alt+↑/↓, плъзгане)
  const moveMember = (key: number, toIndex: number) => {
    const fromIndex = members.findIndex((member) => member.key === key);
    if (fromIndex < 0 || toIndex < 0 || toIndex >= members.length || fromIndex === toIndex)
      return;
    onTouch();
    setOrderTouched(true);
    const next = [...members];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setMembers(next);
  };

  const setPriorityMode = (mode: PriorityMode) => {
    if (mode === priorityMode) return;
    onTouch();
    setOrderTouched(true);
    setPriorityModeState(mode);
  };

  const setHint = (value: string) => {
    onTouch();
    setHintState(value);
  };

  const sortIndexes = useMemo(
    () => sortIndexesForSave(members, priorityMode, orderTouched),
    [members, priorityMode, orderTouched],
  );
  const coefficients = useMemo(() => coefficientsForSave(members), [members]);

  const hasEnoughProducts = members.length >= 2;
  const membersWithoutAmount = members.filter(
    (member) => member.amount == null || !(member.amount > 0),
  );

  const payload: InterchangeGroupPayload | null =
    hasEnoughProducts && coefficients
      ? {
          // групите са ОБЩИ за всички складове — без warehouseId
          hint: hint.trim() || null,
          members: members.map((member, index) => ({
            productId: member.productId,
            coefficient: coefficients[index],
            sortIndex: sortIndexes[index],
          })),
        }
      : null;

  return {
    members,
    hint,
    priorityMode,
    sortIndexes,
    hasEnoughProducts,
    membersWithoutAmount,
    payload,
    load,
    addProducts,
    removeMember,
    setAmount,
    moveMember,
    setPriorityMode,
    setHint,
  };
};
